import { FileIcon } from "@phosphor-icons/react/dist/csr/File";
import { FileArchiveIcon } from "@phosphor-icons/react/dist/csr/FileArchive";
import { FileImageIcon } from "@phosphor-icons/react/dist/csr/FileImage";
import { FileTextIcon } from "@phosphor-icons/react/dist/csr/FileText";
import { FileXlsIcon } from "@phosphor-icons/react/dist/csr/FileXls";
import {
  COLOR_TREATMENTS,
  type ComponentColor,
} from "@volca/ui/tokens/component-colors";
import type { ApAttachmentStatus } from "@/accounts-payable/types";

export const INVOICE_STATUS_TEXT_CLASS = Object.fromEntries(
  Object.entries(COLOR_TREATMENTS).map(([color, treatment]) => [
    color,
    treatment.text,
  ]),
) as Record<ComponentColor, string>;

/**
 * What happened to an attachment that produced no invoices. Said once, in
 * words; an attachment that did produce invoices shows them instead.
 */
export const NO_INVOICE_OUTCOME: Record<
  ApAttachmentStatus,
  { text: string; negative?: boolean }
> = {
  pending: { text: "Waiting to be checked." },
  classifying: { text: "Checking whether this is an invoice…" },
  is_invoice: { text: "Reading the invoice…" },
  // Receipts live in their own tab; the email only notes where this one went.
  is_receipt: { text: "A receipt. It's under Receipts." },
  not_invoice: { text: "Not an invoice, skipped." },
  download_failed: {
    text: "Download failed. We couldn’t get this file from the email.",
    negative: true,
  },
  classification_failed: {
    text: "We couldn’t tell whether this is an invoice.",
    negative: true,
  },
  unsupported_type: {
    text: "Unsupported file type.",
    negative: true,
  },
};

export const fileIconFor = (contentType: string) => {
  if (contentType === "application/pdf" || contentType.startsWith("text/"))
    return FileTextIcon;
  if (contentType.startsWith("image/")) return FileImageIcon;
  if (/spreadsheet|excel|csv/.test(contentType)) return FileXlsIcon;
  if (/zip|compressed|archive/.test(contentType)) return FileArchiveIcon;
  return FileIcon;
};

export const missingInvoicesMessage = (
  expected: number,
  loaded: number,
  loading: boolean,
): string | null => {
  if (loaded >= expected) return null;
  if (loading) return "Loading extracted invoices…";
  if (loaded === 0) return "Couldn’t load the extracted invoices.";
  return `Couldn’t load ${expected - loaded} of the ${expected} extracted invoices.`;
};
